import { useEffect, useRef, useState } from 'react'
import HubClient from 'services/util/hub-client'
import UrlBuilder from 'services/util/url-builder'

const urlBuilder = new UrlBuilder('ai')

type AiResponse = {
	content: string
}

export const useAiConnection = ({
	onReceiveMessage,
}: {
	onReceiveMessage: (sessionId: string, msg: AiResponse) => void
}) => {
	const connectionsRef = useRef<Map<string, HubClient>>(new Map())
	const onReceiveMessageRef = useRef(onReceiveMessage)
	const [loadingSessionIds, setLoadingSessionIds] = useState<Set<string>>(new Set())

	// Keep callback fresh without re-registering event handlers.
	onReceiveMessageRef.current = onReceiveMessage

	const createConnection = (sessionId: string) => {
		const existingConnection = connectionsRef.current.get(sessionId)

		if (existingConnection) {
			return existingConnection
		}

		const connection = new HubClient(urlBuilder.setEndpoint('aichat').url())

		connection.on('ReceiveMessage', (msg: AiResponse) => onReceiveMessageRef.current(sessionId, msg))
		connectionsRef.current.set(sessionId, connection)

		return connection
	}

	const initialize = (sessionId: string) => {
		const connection = createConnection(sessionId)
		connection.start()
	}

	const sendPrompt = (
		sessionId: string,
		prompt: string,
		{ onNext, onComplete }: { onNext: (res: AiResponse) => void; onComplete?: () => void },
	) => {
		const connection = createConnection(sessionId)
		connection.start()

		setLoadingSessionIds((prev) => {
			const next = new Set(prev)
			next.add(sessionId)

			return next
		})

		connection.stream('SendPrompt', { prompt }).subscribe({
			next: onNext,
			error: (res) => {
				setLoadingSessionIds((prev) => {
					const next = new Set(prev)
					next.delete(sessionId)

					return next
				})

				throw new Error(res)
			},
			complete: () => {
				setLoadingSessionIds((prev) => {
					const next = new Set(prev)
					next.delete(sessionId)

					return next
				})

				onComplete?.()
			},
		})
	}

	const isLoading = (sessionId: string) => {
		const connection = connectionsRef.current.get(sessionId)

		return !connection?.isConnected || loadingSessionIds.has(sessionId)
	}

	useEffect(() => {
		const connections = connectionsRef.current

		return () => {
			connections.forEach((connection) => connection.stop())
			connections.clear()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return {
		initialize,
		isLoading,
		sendPrompt,
	}
}
